import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Target, 
  Plus, 
  Flame, 
  Trophy,
  Check,
  Loader2
} from 'lucide-react';

export default function HabitsPage() {
  const { token } = useAuth();
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newHabit, setNewHabit] = useState({ title: '', description: '', frequency: 'daily', target_count: 1 });
  const [creating, setCreating] = useState(false);

  const today = new Date().toISOString().split('T')[0];

  const fetchHabits = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/habits`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHabits(response.data);
    } catch (error) {
      console.error('Error fetching habits:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHabits();
  }, [fetchHabits]);

  const createHabit = async () => {
    if (!newHabit.title.trim()) {
      toast.error('Please enter a habit title');
      return;
    }
    
    setCreating(true);
    try {
      const params = new URLSearchParams({
        title: newHabit.title,
        description: newHabit.description || '',
        frequency: newHabit.frequency,
        target_count: newHabit.target_count
      });
      
      const response = await axios.post(
        `${API}/habits?${params}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Habit created!');
      setShowAddDialog(false);
      setNewHabit({ title: '', description: '', frequency: 'daily', target_count: 1 });
      fetchHabits();
    } catch (error) {
      toast.error('Failed to create habit');
    } finally {
      setCreating(false);
    }
  };

  const completeHabit = async (habitId) => {
    try {
      const response = await axios.post(
        `${API}/habits/complete`,
        { habit_id: habitId, date: today },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.badge_earned) {
        toast.success(`Badge earned: ${response.data.badge_earned}!`);
      } else if (response.data.points) {
        toast.success(`+${response.data.points} points!`);
      } else {
        toast.info(response.data.message);
      }
      
      fetchHabits();
    } catch (error) {
      toast.error('Failed to complete habit');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const completedToday = habits.filter(h => h.completions?.includes(today)).length;
  const totalHabits = habits.length;

  return (
    <div data-testid="habits-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-white uppercase">
            HABIT <span className="text-primary">TRACKER</span>
          </h1>
          <p className="text-gray-400 mt-1">Build consistency, achieve greatness</p>
        </div>
        
        <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
          <DialogTrigger asChild>
            <Button 
              data-testid="add-habit-btn"
              className="bg-primary hover:bg-red-700 text-white uppercase font-semibold"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Habit
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading text-2xl text-white uppercase">
                Create New Habit
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label className="text-gray-300">Habit Title</Label>
                <Input
                  data-testid="habit-title-input"
                  value={newHabit.title}
                  onChange={(e) => setNewHabit({...newHabit, title: e.target.value})}
                  className="bg-secondary border-transparent"
                  placeholder="e.g., Drink 8 glasses of water"
                />
              </div>
              
              <div className="space-y-2">
                <Label className="text-gray-300">Description (Optional)</Label>
                <Input
                  value={newHabit.description}
                  onChange={(e) => setNewHabit({...newHabit, description: e.target.value})}
                  className="bg-secondary border-transparent"
                  placeholder="Why is this habit important?"
                />
              </div>
              
              <Button 
                data-testid="create-habit-btn"
                onClick={createHabit}
                disabled={creating}
                className="w-full bg-primary hover:bg-red-700 text-white uppercase font-semibold"
              >
                {creating ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Create Habit'
                )}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card-tactical">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-gray-400 uppercase text-sm tracking-wider mb-1">Today's Progress</div>
              <div className="flex items-baseline gap-2">
                <span className="font-mono text-4xl font-bold text-white">{completedToday}</span>
                <span className="text-gray-400">/ {totalHabits}</span>
              </div>
            </div>
            <div className="w-16 h-16">
              <svg className="w-full h-full transform -rotate-90">
                <circle cx="32" cy="32" r="28" stroke="#27272A" strokeWidth="4" fill="none" />
                <circle
                  cx="32" cy="32" r="28"
                  stroke="#E31B23"
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray={`${totalHabits > 0 ? (completedToday / totalHabits) * 176 : 0} 176`}
                />
              </svg>
            </div>
          </div>
        </div>
        
        <div className="card-tactical">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-primary/10 rounded-sm flex items-center justify-center">
              <Flame className="w-6 h-6 text-primary" />
            </div>
            <div>
              <div className="text-gray-400 uppercase text-sm tracking-wider">Best Streak</div>
              <div className="font-mono text-2xl font-bold text-white">
                {Math.max(...habits.map(h => h.longest_streak || 0), 0)} days
              </div>
            </div>
          </div>
        </div>
        
        <div className="card-tactical">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-500/10 rounded-sm flex items-center justify-center">
              <Trophy className="w-6 h-6 text-yellow-500" />
            </div>
            <div>
              <div className="text-gray-400 uppercase text-sm tracking-wider">Active Habits</div>
              <div className="font-mono text-2xl font-bold text-white">{totalHabits}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Habits List */}
      {habits.length === 0 ? (
        <div className="text-center py-12 card-tactical">
          <Target className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl text-white mb-2">No habits yet</h3>
          <p className="text-gray-400 mb-4">Start building better habits today!</p>
          <Button 
            onClick={() => setShowAddDialog(true)}
            className="bg-primary hover:bg-red-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Your First Habit
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {habits.map((habit) => {
            const isCompletedToday = habit.completions?.includes(today);
            
            return (
              <div 
                key={habit.id}
                className={`card-tactical flex items-center justify-between ${
                  isCompletedToday ? 'border-primary/50 bg-primary/5' : ''
                }`}
              >
                <div className="flex items-center gap-4">
                  <button
                    data-testid={`complete-habit-${habit.id}`}
                    onClick={() => !isCompletedToday && completeHabit(habit.id)}
                    className={`w-10 h-10 rounded-sm border-2 flex items-center justify-center transition-all ${
                      isCompletedToday 
                        ? 'bg-primary border-primary' 
                        : 'border-gray-600 hover:border-primary'
                    }`}
                  >
                    {isCompletedToday && <Check className="w-5 h-5 text-white" />}
                  </button>
                  
                  <div>
                    <h3 className={`font-medium ${isCompletedToday ? 'text-gray-400 line-through' : 'text-white'}`}>
                      {habit.title}
                    </h3>
                    {habit.description && (
                      <p className="text-sm text-gray-500">{habit.description}</p>
                    )}
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-primary">
                      <Flame className="w-4 h-4" />
                      <span className="font-mono font-bold">{habit.current_streak}</span>
                    </div>
                    <div className="text-xs text-gray-500">Current streak</div>
                  </div>
                  
                  <div className="text-right">
                    <div className="flex items-center gap-1 text-yellow-500">
                      <Trophy className="w-4 h-4" />
                      <span className="font-mono font-bold">{habit.longest_streak}</span>
                    </div>
                    <div className="text-xs text-gray-500">Best</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
